import { StateCreator } from 'zustand';
import { AuthState, UserRole, SignUpCredentials, SignInCredentials, OAuthProvider, User } from '../../domain';
import authService, { AccountNotAllowedError } from '../../services/auth';
import { localModeService } from '../../services/localModeService';
import type { AppStore } from '../index';

export interface AuthSlice {
  auth: AuthState & {
    /** Who is using the app, and how: set by initApp alone, once a session (or its absence) has
     * been settled, so a sign-in is never acted on before it's been let in. */
    updateUser: (user: User | null, mode: AuthState['mode']) => Promise<void>;
    signUp: (credentials: SignUpCredentials) => Promise<void>;
    signIn: (credentials: SignInCredentials) => Promise<void>;
    signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
    completeRedirectSignIn: () => Promise<void>;
    signOut: () => Promise<void>;
    hasRole: (requiredRole: UserRole | UserRole[]) => boolean;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearError: () => void;
    setRedirectError: (redirectError: string | null) => void;
    clearAccountRefused: () => void;
    /** Offers what was made in local mode to the account just signed into (initApp). */
    offerLocalData: (offer: AuthState['localDataOffer']) => void;
    /** The user's yes to the offer: carries it into the account and pushes it. False, and the offer
     * still open, if that failed. */
    addLocalDataToAccount: () => Promise<boolean>;
    /** The user's "not now": it stays on the phone, out of sight while signed in, and comes back on
     * signing out; the next sign-in offers it again. */
    keepLocalDataOnPhone: () => void;
    /** The user's "delete them": gone from the phone, for good. False, and the offer still open, if
     * that failed. */
    deleteLocalData: () => Promise<boolean>;
  };
}

export const createAuthSlice: StateCreator<AppStore, [], [], AuthSlice> = (set, get) => {
  // A sign-in's result isn't put in the store here: onAuthStateChanged reports the session to
  // initApp, which waits until it's been let in (authService.awaitSignInCheck), carries any local
  // data into it, and then calls updateUser. These only report how the attempt itself went.
  const failed = (error: unknown, fallback: string) => {
    const refused = error instanceof AccountNotAllowedError;
    set((state) => ({
      auth: {
        ...state.auth,
        loading: false,
        error: refused ? null : error instanceof Error ? error.message : fallback,
        accountRefused: refused,
      },
    }));
  };

  return {
    auth: {
      user: null,
      roles: [],
      loading: true, // Start with true because we're checking Firebase session
      error: null,
      redirectError: null,
      mode: null,
      accountRefused: false,
      localDataOffer: null,

      updateUser: async (user: User | null, mode: AuthState['mode']) => {
        set((state) => ({
          auth: {
            ...state.auth,
            user,
            roles: user?.roles ?? [],
            mode: user ? mode : null,
            loading: false,
            error: null,
            ...(mode === 'account' && { redirectError: null, accountRefused: false }),
            // An offer is made to an account; signed out of it, there's no one to make it to.
            ...(mode !== 'account' && { localDataOffer: null }),
          },
        }));
      },

      signUp: async (credentials: SignUpCredentials) => {
        set((state) => ({ auth: { ...state.auth, loading: true, error: null, accountRefused: false } }));
        try {
          await authService.signUp(credentials);
        } catch (error) {
          failed(error, 'Sign up failed');
          throw error;
        }
      },

      signIn: async (credentials: SignInCredentials) => {
        set((state) => ({ auth: { ...state.auth, loading: true, error: null, accountRefused: false } }));
        try {
          await authService.signIn(credentials);
        } catch (error) {
          failed(error, 'Sign in failed');
          throw error;
        }
      },

      signInWithOAuth: async (provider: OAuthProvider) => {
        set((state) => ({ auth: { ...state.auth, loading: true, error: null, accountRefused: false } }));
        try {
          // Redirect flow: navigation is under way and this resolves with nothing — the returning
          // session is picked up by completeRedirectSignIn / onAuthStateChanged.
          await authService.signInWithOAuth(provider);
        } catch (error) {
          failed(error, `Sign in with ${provider} failed`);
          throw error;
        }
      },

      /**
       * Drains a pending signInWithRedirect result once, when the app boots after
       * a mobile / installed-PWA OAuth redirect. onAuthStateChanged already
       * establishes the session, so this surfaces the redirect leg's errors —
       * which would otherwise be swallowed across the full-page reload — and
       * that it was turned away for want of a code.
       */
      completeRedirectSignIn: async () => {
        try {
          await authService.getOAuthRedirectResult();
        } catch (error) {
          if (error instanceof AccountNotAllowedError) {
            set((state) => ({ auth: { ...state.auth, accountRefused: true } }));
          } else {
            const message = error instanceof Error ? error.message : 'Sign in failed';
            set((state) => ({ auth: { ...state.auth, redirectError: message } }));
          }
        }
      },

      /** Signing out leads back to local mode, which initApp enters when the session is gone. */
      signOut: async () => {
        set((state) => ({ auth: { ...state.auth, loading: true, error: null } }));
        try {
          await authService.signOut();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign out failed';
          set((state) => ({
            auth: { ...state.auth, loading: false, error: message },
          }));
          throw error;
        }
      },

      /**
       * Returns true if the user holds at least one of the required role(s).
       */
      hasRole: (requiredRole: UserRole | UserRole[]) => {
        const userRoles = get().auth.roles;
        if (!userRoles.length) return false;

        const required = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
        return required.some((r) => userRoles.includes(r));
      },

      setLoading: (loading: boolean) => {
        set((state) => ({ auth: { ...state.auth, loading } }));
      },

      setError: (error: string | null) => {
        set((state) => ({ auth: { ...state.auth, error } }));
      },

      clearError: () => {
        set((state) => ({ auth: { ...state.auth, error: null } }));
      },

      setRedirectError: (redirectError: string | null) => {
        set((state) => ({ auth: { ...state.auth, redirectError } }));
      },

      clearAccountRefused: () => {
        set((state) => ({ auth: { ...state.auth, accountRefused: false } }));
      },

      offerLocalData: (localDataOffer) => {
        set((state) => ({ auth: { ...state.auth, localDataOffer } }));
      },

      addLocalDataToAccount: async () => {
        const { user, mode } = get().auth;
        if (!user || mode !== 'account') return false;
        try {
          const moved = await localModeService.moveToAccount({
            uid: user.uid,
            displayName: user.displayName || user.email || 'Member',
          });
          set((state) => ({ auth: { ...state.auth, localDataOffer: null } }));
          // Local mode's offer to undo a completion goes with it: its queued action has just been
          // replaced by the uploads.
          get().chores.dismissUndoableCompletion();
          await get().chores.load();
          // Pushed now rather than when the user next taps the sync status, so it reaches the
          // account's other devices.
          if (moved) await get().chores.syncRemoteFromFirestore();
          return true;
        } catch (error) {
          // moveToAccount is one transaction: failed, nothing has changed, and it can be tried again.
          console.error('Could not add the local data to the account:', error);
          return false;
        }
      },

      keepLocalDataOnPhone: () => {
        set((state) => ({ auth: { ...state.auth, localDataOffer: null } }));
      },

      deleteLocalData: async () => {
        try {
          await localModeService.deleteLocalData();
          set((state) => ({ auth: { ...state.auth, localDataOffer: null } }));
          return true;
        } catch (error) {
          // One transaction: failed, nothing has been deleted.
          console.error('Could not delete the local data:', error);
          return false;
        }
      },
    },
  };
};
